import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Input,
  useTheme,
  spacing,
} from '@fti/design-system';

function normalizeEdgeType(input: string): string {
  return input.trim().replace(/\s+/g, '_').toUpperCase();
}

interface EdgeTypeComboboxProps {
  value: string;
  onChange: (value: string) => void;
  existingTypes: string[];
}

export const EdgeTypeCombobox: React.FC<EdgeTypeComboboxProps> = ({ value, onChange, existingTypes }) => {
  const { theme } = useTheme();
  const [text, setText] = useState(value);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  const filtered = useMemo(() => {
    if (!text.trim()) return existingTypes.slice(0, 10);
    const lower = text.toLowerCase();
    return existingTypes.filter((t) => t.toLowerCase().includes(lower)).slice(0, 10);
  }, [text, existingTypes]);

  const normalized = text.trim() ? normalizeEdgeType(text) : '';
  const isNew = normalized && !existingTypes.includes(normalized);

  const selectType = (type: string) => {
    setText(type);
    onChange(type);
    setShowDropdown(false);
    setHighlightIndex(0);
  };

  const handleBlur = () => {
    // Delay to allow click on dropdown item
    setTimeout(() => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
        if (text.trim()) {
          const n = normalizeEdgeType(text);
          setText(n);
          onChange(n);
        }
      }
    }, 150);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || filtered.length === 0) {
      if (e.key === 'Enter' && text.trim()) {
        const n = normalizeEdgeType(text);
        setText(n);
        onChange(n);
      }
      return;
    }

    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      selectType(filtered[highlightIndex]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', minWidth: '120px' }}>
      <Input
        label="Type"
        size="sm"
        value={text}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setText(e.target.value);
          onChange(e.target.value);
          setShowDropdown(true);
          setHighlightIndex(0);
        }}
        onFocus={() => setShowDropdown(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="e.g., OWNS"
      />
      {isNew && text.trim() && (
        <div style={{ fontSize: '0.7rem', color: theme.colors.text.secondary, marginTop: '2px' }}>
          New type: <strong>{normalized}</strong>
        </div>
      )}
      {showDropdown && filtered.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          width: '100%',
          zIndex: 99999,
          background: theme.colors.background.elevated,
          border: `1px solid ${theme.colors.border.default}`,
          borderRadius: '4px',
          maxHeight: '200px',
          overflow: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          marginTop: '2px',
        }}>
          {filtered.map((type, i) => (
            <div
              key={type}
              style={{
                padding: `${spacing.xs} ${spacing.sm}`,
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: theme.colors.text.primary,
                background: i === highlightIndex ? theme.colors.background.paper : 'transparent',
                borderBottom: `1px solid ${theme.colors.border.default}`,
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                selectType(type);
              }}
              onMouseEnter={() => setHighlightIndex(i)}
            >
              {type}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
